/**
 * Tajwīd module — index + lesson detail pages.
 *
 *   /cours/tajwid           → TajwidIndex (cards, one per rule)
 *   /cours/tajwid/:rule     → TajwidLesson (single rule with isolated examples + audio)
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookOpen, Sparkles, Play, Pause, Loader2, Trophy } from 'lucide-react';
import { tajwidApi, lexicalApi, quranApi } from '../services/api';
import { GlassCard, PageLoading, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAudio } from '../context/AudioContext';
import TajwidColoredText from '../components/quran/TajwidColoredText';
import { ExercisesHub } from '../components/courses/ExercisesHub';
import { SkillResultsPanel } from '../components/courses/SkillResultsPanel';
import { CurriculumJourney } from '../components/courses/CurriculumJourney';
import { JourneyCompetencyRunner } from '../components/courses/JourneyCompetencyRunner';

export const TajwidIndex = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('journey'); // 'journey' | 'rules'
  const [running, setRunning] = useState(null);
  const [journeyKey, setJourneyKey] = useState(0);

  useEffect(() => {
    tajwidApi.getLessons()
      .then((r) => setLessons(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoading />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="tajwid-index-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="tajwid-back-to-cours"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('cours.back_to_cours')}
      </Button>

      <SectionHeader
        overline={t('tajwid.overline')}
        title={t('tajwid.title')}
        subtitle={t('tajwid.subtitle')}
      />

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex bg-surface-elevated/60 border border-border rounded-md p-1" data-testid="tajwid-index-view-tabs">
          <button
            type="button"
            onClick={() => setView('journey')}
            className={`px-3 py-1 rounded text-xs transition-colors ${
              view === 'journey' ? 'bg-primary/20 text-primary' : 'text-text-muted hover:text-text-primary'
            }`}
            data-testid="tajwid-index-view-journey"
          >
            Parcours
          </button>
          <button
            type="button"
            onClick={() => setView('rules')}
            className={`px-3 py-1 rounded text-xs transition-colors ${
              view === 'rules' ? 'bg-primary/20 text-primary' : 'text-text-muted hover:text-text-primary'
            }`}
            data-testid="tajwid-index-view-rules"
          >
            Toutes les règles
          </button>
        </div>
        <Link to="/cours/tajwid/leaderboard">
          <Button
            variant="outline"
            size="sm"
            data-testid="tajwid-open-leaderboard"
            className="text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          >
            <Trophy className="w-3.5 h-3.5 mr-1.5" />
            {t('tajwid_leaderboard.cta')}
          </Button>
        </Link>
      </div>

      {view === 'journey' && (
        <div className="mt-6" data-testid="tajwid-index-journey">
          {running ? (
            <JourneyCompetencyRunner
              competency={running.competency}
              difficulty={running.difficulty}
              onExit={() => {
                setRunning(null);
                setJourneyKey((k) => k + 1);
              }}
              testidPrefix="tajwid-journey-runner"
            />
          ) : (
            <CurriculumJourney
              key={journeyKey}
              curriculumId="curr_tajweed_beginner"
              onStartCompetency={(c, d) => {
                // For application/memorization steps without exercises, jump
                // directly to the practiceHref instead of starting a series.
                if (c.practiceHref && (c.skill === 'tajweed_application' || c.skill === 'memorization_tajweed')) {
                  navigate(c.practiceHref);
                  return;
                }
                setRunning({ competency: c, difficulty: d });
              }}
              testidPrefix="tajwid-journey"
            />
          )}
        </div>
      )}

      {view === 'rules' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {lessons.map((l, idx) => (
          <motion.div
            key={l.rule}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(idx * 0.04, 0.3) }}
          >
            <Link to={`/cours/tajwid/${l.rule}`} data-testid={`tajwid-lesson-card-${l.rule}`}>
              <GlassCard
                className="h-full hover:border-primary/40 transition-all cursor-pointer"
                style={{ borderLeftColor: l.color, borderLeftWidth: 4 }}
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge style={{ backgroundColor: l.color + '22', color: l.color, borderColor: l.color + '55' }}>
                    {l.label}
                  </Badge>
                  <BookOpen className="w-4 h-4 text-text-muted" />
                </div>
                <h3 className="font-serif text-lg text-text-primary mb-1">{l.title}</h3>
                <p className="text-xs text-text-muted mb-3">{l.subtitle}</p>
                <p className="text-sm text-text-secondary leading-relaxed line-clamp-3">
                  {l.summary}
                </p>
              </GlassCard>
            </Link>
          </motion.div>
        ))}
        </div>
      )}
    </div>
  );
};

/**
 * Filter annotations to a single rule code so the example shows only the
 * current rule isolated, not all detected rules at once.
 */
const isolateRule = (annotations, rule) =>
  (annotations || []).filter((a) => a.rule === rule);

const ExampleAyahCard = ({ example, rule, ruleLabel }) => {
  const { t } = useTranslation();
  const { state: audioState, playAyah } = useAudio();
  const [ann, setAnn] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [s, a] = (example.ayah || '').split(':').map((x) => parseInt(x, 10));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      lexicalApi.getAyahTajwid(example.ayah).then((r) => r.data, () => null),
      // We need globalAyahNumber + hizbNumber for offline playback resolution.
      // Pull just this one verse from the surah list.
      quranApi.getVerses(s).then(
        (r) => (r.data || []).find((v) => v.verse_number === a) || null,
        () => null,
      ),
    ]).then(([annData, verseMeta]) => {
      if (cancelled) return;
      setAnn(annData);
      setMeta(verseMeta);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [example.ayah, s, a]);

  const isPlayingThis = audioState.isPlaying
    && audioState.currentTrack?.ayahKey === example.ayah;

  const onPlay = () => {
    if (!meta) return;
    playAyah({
      surahNumber: meta.surah_number,
      ayahNumber: meta.verse_number,
      ayahKey: meta.verse_key,
      globalAyahNumber: meta.global_ayah_number,
      hizbNumber: meta.hizb_number,
    });
  };

  const isolated = isolateRule(ann?.annotations || [], rule);
  const allCount = ann?.annotations?.length || 0;
  const isolatedCount = isolated.length;

  return (
    <div
      className="p-4 rounded-lg bg-surface-elevated/40 border border-border"
      data-testid={`tajwid-example-${example.ayah}`}
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <Link
          to={`/quran/${s}`}
          className="text-xs text-primary hover:underline"
        >
          {t('tajwid.surah_label')} {example.ayah}
        </Link>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-text-muted">
            {isolatedCount > 0
              ? t('tajwid.rule_count_isolated', { rule: ruleLabel, n: isolatedCount })
              : t('tajwid.rule_count_none', { rule: ruleLabel })}
            {allCount > isolatedCount && (
              <> · {t('tajwid.rule_count_total', { n: allCount })}</>
            )}
          </span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onPlay}
            disabled={!meta || loading}
            className="h-8 text-primary hover:bg-primary/10"
            aria-label={t('tajwid.play_ayah')}
            data-testid={`tajwid-example-play-${example.ayah}`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isPlayingThis ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      <p
        className="quran-arabic-text text-text-primary leading-loose text-right"
        dir="rtl"
      >
        {ann?.text ? (
          <TajwidColoredText text={ann.text} annotations={isolated} />
        ) : (
          example.highlight || ''
        )}
      </p>
      {example.note && (
        <p className="text-xs text-text-muted mt-2 italic">{example.note}</p>
      )}
    </div>
  );
};

export const TajwidLesson = () => {
  const { t } = useTranslation();
  const { rule } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    tajwidApi.getLesson(rule)
      .then((r) => setLesson(r.data))
      .catch(() => setLesson(null))
      .finally(() => setLoading(false));
  }, [rule]);

  if (loading) return <PageLoading />;
  if (!lesson) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-text-muted">{t('tajwid.not_found')}</p>
        <Button onClick={() => navigate('/cours/tajwid')} className="mt-4">
          {t('tajwid.back_to_index')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`tajwid-lesson-${rule}`}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours/tajwid')}
        className="text-text-muted hover:text-text-primary mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('tajwid.back_to_index')}
      </Button>

      <div className="mb-8">
        <Badge
          className="mb-3"
          style={{ backgroundColor: lesson.color + '22', color: lesson.color,
                   borderColor: lesson.color + '55' }}
        >
          {lesson.label} · {lesson.harakat} ḥarakāt
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-serif text-text-primary">{lesson.title}</h1>
        <p className="text-base text-text-muted mt-2">{lesson.subtitle}</p>
      </div>

      <GlassCard className="mb-6">
        <h2 className="overline mb-2">{t('tajwid.section_intro')}</h2>
        <p className="text-text-secondary leading-relaxed">{lesson.summary}</p>
      </GlassCard>

      <Tabs defaultValue="lesson" className="space-y-4" data-testid="tajwid-inner-tabs">
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="lesson" data-testid="tajwid-tab-lesson">Leçon</TabsTrigger>
          <TabsTrigger value="exercises" data-testid="tajwid-tab-exercises">Exercices</TabsTrigger>
          <TabsTrigger value="practice" data-testid="tajwid-tab-practice">Pratiquer dans le Coran</TabsTrigger>
          <TabsTrigger value="results" data-testid="tajwid-tab-results">Mes résultats</TabsTrigger>
        </TabsList>

        <TabsContent value="lesson" className="space-y-4">
          <GlassCard>
            <h2 className="overline mb-2">{t('tajwid.section_rule')}</h2>
            <p className="text-text-secondary leading-relaxed">
              {lesson.howToRead || lesson.rule /* backwards-compat: legacy field */}
            </p>
            {lesson.letters?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-text-muted mb-2">
                  {t('tajwid.letters_concerned')}
                </p>
                <div className="flex flex-wrap gap-2 font-arabic" dir="rtl">
                  {lesson.letters.map((l, i) => (
                    <Badge key={i} variant="outline" className="font-arabic text-lg px-3 py-1">{l}</Badge>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>

          {lesson.examples?.length > 0 && (
            <GlassCard>
              <h2 className="overline mb-1 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                {t('tajwid.section_examples')}
              </h2>
              <p className="text-xs text-text-muted mb-4">
                {t('tajwid.examples_isolated_hint', { rule: lesson.label })}
              </p>
              <div className="space-y-4">
                {lesson.examples.map((ex, i) => (
                  <ExampleAyahCard
                    key={`${ex.ayah}-${i}`}
                    example={ex}
                    rule={rule}
                    ruleLabel={lesson.label}
                  />
                ))}
              </div>
            </GlassCard>
          )}
        </TabsContent>

        <TabsContent value="exercises">
          <ExercisesHub
            section="tajweed"
            ruleId={rule}
            title={`Séries · ${lesson.label}`}
            testidPrefix={`tajwid-hub-${rule}`}
          />
        </TabsContent>

        <TabsContent value="practice" className="space-y-3">
          <GlassCard>
            <h3 className="overline mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Pratiquer dans le Coran
            </h3>
            <p className="text-sm text-text-secondary mb-4">
              Activez le mode « Exercice » directement dans une sourate pour identifier
              les occurrences de <strong>{lesson.label}</strong> au fil du texte. Votre
              score est sauvegardé automatiquement.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => navigate('/quran/1')}
                      data-testid={`tajwid-practice-go-fatiha-${rule}`}>
                Al-Fātiḥa (1)
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/quran/112')}
                      data-testid={`tajwid-practice-go-ikhlas-${rule}`}>
                Al-Ikhlāṣ (112)
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate('/quran')}
                      data-testid={`tajwid-practice-go-list-${rule}`}>
                Choisir une sourate…
              </Button>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="results">
          <SkillResultsPanel
            section="tajweed"
            ruleId={rule}
            practiceHref="/quran/1"
            testidPrefix={`tajwid-results-${rule}`}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
